import { CheckOutlined } from '@ant-design/icons';
import { Input, message, Modal, ModalProps, Space, Typography } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import { RequestJobPostingDetail, RequestJobPostingStageWithApprover } from '../../types';
import Form from '../Form';
import ConfirmRequestJobPostingApprovalModal from './ConfirmRequestJobPostingApprovalModal';

interface ApproveRequestOrStageFormValues {
  comment?: string;
}

export interface ApproveRequestOrStageModalProps extends ModalProps {
  requestJobPostingId: RequestJobPostingDetail['id'];
  isLastStage: boolean;
  currentStage: RequestJobPostingStageWithApprover;
  onSuccess?: (
    data: void,
    variables: ApproveRequestOrStageFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const ApproveRequestOrStageModal = ({
  requestJobPostingId,
  isLastStage,
  currentStage,
  onSuccess,
  ...props
}: ApproveRequestOrStageModalProps) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [openConfirmRequestJobPostingApproval, setOpenConfirmRequestJobPostingApproval] =
    useState(false);

  const requestJobPosting = useAPIQuery(`/request-job-postings/${requestJobPostingId}/`);

  const mutation = useAPIMutation(
    async ({ comment }: ApproveRequestOrStageFormValues, api) => {
      await api.post(`/request-job-posting-stage-actions/${currentStage.id}/approve/`, {
        comment: comment || '',
      });
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries('/request-job-postings/');
      },
      onSuccess: async (data, variables, context) => {
        const requestJobPosting = await queryClient.fetchQuery<RequestJobPostingDetail>(
          `/request-job-postings/${requestJobPostingId}/`
        );

        if (requestJobPosting.status === 'APPROVED') {
          message.success(t('Requisição aprovada.'));
        } else {
          message.success(t('Etapa aprovada.'));
        }

        onSuccess && onSuccess(data, variables, context);
      },
      onError: (error, variables, context) => {
        message.error(error.message);
      },
    }
  );

  return (
    <>
      <ConfirmRequestJobPostingApprovalModal
        key={`ConfirmRequestJobPostingApprovalModal-${requestJobPostingId}`}
        requestJobPostingName={requestJobPosting.data?.name || ''}
        open={openConfirmRequestJobPostingApproval}
        onOk={() => {
          form.submit();
          setOpenConfirmRequestJobPostingApproval(false);
        }}
        onCancel={() => setOpenConfirmRequestJobPostingApproval(false)}
      />
      <Modal
        title={
          isLastStage ? (
            <Typography.Text strong>
              <Trans>Aprovar requisição</Trans>
            </Typography.Text>
          ) : (
            <Typography.Text strong>
              <Trans>Aprovar etapa</Trans>
            </Typography.Text>
          )
        }
        okButtonProps={{
          htmlType: 'submit',
          icon: <CheckOutlined />,
          loading: mutation.isLoading,
        }}
        okText={t('Aprovar')}
        onOk={() => {
          if (isLastStage) {
            setOpenConfirmRequestJobPostingApproval(true);
          } else {
            form.submit();
          }
        }}
        {...props}
      >
        <Helmet>
          <title>
            {isLastStage ? t('Aprovar requisição') : t('Aprovar etapa')} |{' '}
            {t('Recrutamento e Seleção')}
          </title>
        </Helmet>
        <Space direction="vertical">
          {isLastStage ? (
            <Typography.Text>
              <Trans>Ao confirmar, a requisição</Trans>{' '}
              <Typography.Text strong>{requestJobPosting.data?.name}</Typography.Text>{' '}
              <Trans>será aprovada e finalizada.</Trans>
            </Typography.Text>
          ) : (
            <Typography.Text>
              <Trans>Ao confirmar, esta etapa da requisição</Trans>{' '}
              <Typography.Text strong>{requestJobPosting.data?.name}</Typography.Text>{' '}
              <Trans>será aprovada.</Trans>
            </Typography.Text>
          )}
          <Form<ApproveRequestOrStageFormValues>
            form={form}
            layout="vertical"
            onFinish={async (values) => {
              mutation.mutate(values);
            }}
          >
            <Form.Item label={t('Comentários')} name="comment">
              <Input.TextArea maxLength={4000} rows={4} showCount />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    </>
  );
};

export default ApproveRequestOrStageModal;
